import { Button } from "@mantine/core";
import { t } from "i18next";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useConfiguratorComponents } from "../../../hooks/useConfiguratorComponents";
import { usePersons } from "../../../hooks/usePersons";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { Connection, Storage } from "../../../utils/backend-types";
import { ObjectName } from "../../../utils/enums";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { BATTERY_STORAGE_FORM_DATA } from "./BatteryStorageModalForm.constants";

type BatteryStorageModalFormProps = {
  siteId: number;
  variantId: number;
  onClose: () => void;
  onSubmit: (data: Storage) => Promise<Storage>;
} & ({ mode: "create" } | { mode: "edit"; storage: Storage });

function BatteryStorageModalForm({
  siteId,
  variantId,
  onClose,
  onSubmit,
  ...modeProps
}: BatteryStorageModalFormProps) {
  const { showErrorToast } = useToast();
  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { isSubmitting }
  } = useForm<Storage>({
    defaultValues: modeProps.mode === "edit" ? modeProps.storage : undefined
  });
  // A non-field server error leaves RHF's form errors empty, so its own
  // isSubmitSuccessful would report the failed submit as a success.
  const [hasSubmitSucceeded, setHasSubmitSucceeded] = useState(false);

  const {
    persons,
    isLoading: isPersonsLoading,
    error: personsError
  } = usePersons({
    variantId
  });
  const {
    data: connections,
    isLoading: isConnectionsLoading,
    error: connectionsError
  } = useConfiguratorComponents<Connection>(ObjectName.Connection, siteId);

  const collectedIsLoading = isPersonsLoading || isConnectionsLoading;
  const collectedError = personsError || connectionsError;

  const personOptions = persons?.map((person) => ({
    value: person.id,
    label: person.name
  }));
  const connectionOptions = connections?.map((connection) => ({
    value: connection.id,
    label: connection.name
  }));

  const onValidSubmit: SubmitHandler<Storage> = async (data) => {
    setHasSubmitSucceeded(false);

    try {
      await onSubmit(data);
      setHasSubmitSucceeded(true);
    } catch (error) {
      setErrorsFromResponseData<Storage>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit(onValidSubmit)}>
      <LoadOrError error={collectedError} loading={collectedIsLoading}>
        <FormFieldController
          control={control}
          data={BATTERY_STORAGE_FORM_DATA.name}
        />
        <FormFieldController
          control={control}
          data={{
            ...BATTERY_STORAGE_FORM_DATA.person,
            data: personOptions
          }}
        />
        <FormFieldController
          control={control}
          data={{
            ...BATTERY_STORAGE_FORM_DATA.connection,
            data: connectionOptions
          }}
        />
        <FormFieldController
          control={control}
          data={BATTERY_STORAGE_FORM_DATA.commissioning}
        />
        <FormFieldController
          control={control}
          data={BATTERY_STORAGE_FORM_DATA.decommissioning}
        />
        <FormFieldController
          control={control}
          data={BATTERY_STORAGE_FORM_DATA.storageSize}
        />
        <FormFieldController
          control={control}
          data={BATTERY_STORAGE_FORM_DATA.installedCapacity}
        />
        <FormFieldController
          control={control}
          data={BATTERY_STORAGE_FORM_DATA.efficiency}
        />
        <FormFieldController
          control={control}
          data={BATTERY_STORAGE_FORM_DATA.standbyConsumption}
        />
      </LoadOrError>

      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          disabled={collectedIsLoading || !!collectedError}
          loading={isSubmitting || hasSubmitSucceeded}
          type="submit"
        >
          Speichern
        </Button>
      </ModalFooter>
    </form>
  );
}

export { BatteryStorageModalForm, BatteryStorageModalFormProps };
